import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import {
  User, Mail, CreditCard, Briefcase, Building2,
  MapPin, Calendar, Globe, Clock, Lock, Award,
  FolderOpen, MessageSquare, Network, GraduationCap, FileCheck,
  Fingerprint, ClipboardList, ShieldCheck, LayoutDashboard, UserCircle,
  CheckCircle2, AlertCircle, Settings2, Check, X, Loader2, KeyRound
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MODULES = [
  { id: 1, name: 'Carpeta de Arranque', icon: FolderOpen, color: '#E6A817', externalUrl: 'https://arranque.tysta.cl/' },
  { id: 2, name: 'GCOM', icon: MessageSquare, color: '#D35400', externalUrl: 'https://arranque.tysta.cl/acceso_gcom' },
  { id: 3, name: 'Organigrama', icon: Network, color: '#5B7D9A', route: '/organigrama-viewer' },
  { id: 4, name: 'Agendamiento de Capacitaciones', icon: GraduationCap, color: '#1B919B', route: '/capacitacion-viewer' },
  { id: 5, name: 'Control Documental', icon: FileCheck, color: '#6B8E8E', route: '/documentos-viewer' },
  { id: 6, name: 'APP Pase Digital', icon: Fingerprint, color: '#6C3D8E', isApp: true },
  { id: 7, name: 'Actividades de Liderazgo', icon: ClipboardList, color: '#a05e49', route: '/liderazgo-viewer' },
  { id: 8, name: 'Checklist Digital', icon: ShieldCheck, color: '#2E7D32', route: '/herramientas-viewer' },
  { id: 9, name: 'Centro de Datos', icon: LayoutDashboard, color: '#7C3AED', route: '/centro-datos' },
  { id: 13, name: 'Reporte de Incidente', icon: AlertCircle, color: '#DC2626', route: '/incidentes' },
];

const formatRut = (rut) => {
  if (!rut) return '-';
  const clean = rut.replace(/[^0-9kK]/g, '');
  if (clean.length < 2) return rut;
  const body = clean.slice(0, -1);
  const dv = clean.slice(-1).toUpperCase();
  const formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${formatted}-${dv}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

const InfoRow = ({ icon: Icon, label, value, iconColor = 'text-slate-400' }) => (
  <div className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0">
    <div className={`mt-0.5 shrink-0 ${iconColor}`}>
      <Icon className="w-3.5 h-3.5" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-xs font-medium text-slate-900 truncate">{value || '-'}</p>
    </div>
  </div>
);

export const PortalPerfil = ({ onSelectTab, hideInfoPanels = false }) => {
  const navigate = useNavigate();
  const { user: authUser, token } = useAuth();
  // Fall back to cached user on hard reloads where AuthContext is still hydrating
  const cachedUser = useMemo(() => {
    try { const c = localStorage.getItem('cached_user'); return c ? JSON.parse(c) : null; } catch { return null; }
  }, []);
  const user = authUser || cachedUser;
  const [capStats, setCapStats] = useState(null); // { total, aprobadas, porcentaje }
  const [liderazgoStats, setLiderazgoStats] = useState(null); // { total, cumplidas, pendientes, porcentaje }
  const [showAppModal, setShowAppModal] = useState(false);

  // Seleccion de Areas y Actividades del trabajador
  const [selData, setSelData] = useState(null); // { areas, actividades, selected_area_ids, selected_actividad_ids, area_principal_id }
  const [showSelModal, setShowSelModal] = useState(false);
  const [draftAreas, setDraftAreas] = useState([]);
  const [draftActs, setDraftActs] = useState([]);
  const [savingSel, setSavingSel] = useState(false);
  const [selSearch, setSelSearch] = useState('');

  // Modal de detalle pendientes
  const [pendCapModal, setPendCapModal] = useState(false);
  const [pendCapList, setPendCapList] = useState(null); // null = loading, [] = vacio
  const [pendLidModal, setPendLidModal] = useState(false);
  const [pendLidList, setPendLidList] = useState(null);

  // Cambiar contraseña
  const [pwdModal, setPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState({ actual: '', nueva: '', confirmar: '' });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState(false);

  const submitPwd = async () => {
    setPwdError('');
    if (!pwdForm.actual || !pwdForm.nueva || !pwdForm.confirmar) {
      setPwdError('Complete todos los campos.'); return;
    }
    if (pwdForm.nueva.length < 4) {
      setPwdError('La nueva contraseña debe tener al menos 4 caracteres.'); return;
    }
    if (pwdForm.nueva !== pwdForm.confirmar) {
      setPwdError('La confirmación no coincide con la nueva contraseña.'); return;
    }
    setPwdBusy(true);
    try {
      await axios.post(`${API}/trabajador/cambiar-password`,
        { password_actual: pwdForm.actual, password_nueva: pwdForm.nueva },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPwdSuccess(true);
      setPwdForm({ actual: '', nueva: '', confirmar: '' });
      setTimeout(() => { setPwdModal(false); setPwdSuccess(false); }, 1400);
    } catch (err) {
      setPwdError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    }
    setPwdBusy(false);
  };

  const loadResumen = (headers) => {
    axios.get(`${API}/trabajador/mis-cursos-resumen`, { headers })
      .then(r => {
        const d = r.data || {};
        setCapStats({
          total: d.total || 0,
          aprobadas: d.aprobadas || 0,
          porcentaje: d.porcentaje || 0,
        });
      })
      .catch(() => setCapStats({ total: 0, aprobadas: 0, porcentaje: 0 }));
  };

  useEffect(() => {
    if (!token || !user?.id) return;
    const headers = { Authorization: `Bearer ${token}` };

    loadResumen(headers);

    axios.get(`${API}/trabajador/mis-areas-actividades`, { headers })
      .then(r => setSelData(r.data || null))
      .catch(() => setSelData(null));

    axios.get(`${API}/liderazgo/mis-actividades`, { headers })
      .then(r => {
        const total = r.data?.total || 0;
        const cumplidas = r.data?.cumplidas || 0;
        const pendientes = r.data?.pendientes || 0;
        const porcentaje = total > 0 ? Math.round((cumplidas / total) * 100) : 0;
        setLiderazgoStats({ total, cumplidas, pendientes, porcentaje });
      })
      .catch(() => setLiderazgoStats(null));
  }, [token, user?.id]);

  // useMemo hooks must run in same order on every render (before early return)
  const selectedAreaNames = useMemo(() => {
    if (!selData) return [];
    const map = Object.fromEntries((selData.areas || []).map(a => [a.id, a.nombre]));
    return (selData.selected_area_ids || []).map(id => map[id]).filter(Boolean);
  }, [selData]);

  const selectedActNames = useMemo(() => {
    if (!selData) return [];
    const map = Object.fromEntries((selData.actividades || []).map(a => [a.id, a.nombre]));
    return (selData.selected_actividad_ids || []).map(id => map[id]).filter(Boolean);
  }, [selData]);

  const filteredAreas = useMemo(() => {
    if (!selData) return [];
    const q = selSearch.trim().toLowerCase();
    if (!q) return selData.areas || [];
    return (selData.areas || []).filter(a => a.nombre.toLowerCase().includes(q));
  }, [selData, selSearch]);

  const filteredActs = useMemo(() => {
    if (!selData) return [];
    const q = selSearch.trim().toLowerCase();
    if (!q) return selData.actividades || [];
    return (selData.actividades || []).filter(a => a.nombre.toLowerCase().includes(q));
  }, [selData, selSearch]);

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3" data-testid="perfil-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
        <p className="text-xs text-slate-500">Cargando tu perfil...</p>
        <button
          onClick={() => window.location.reload()}
          className="text-xs text-teal-600 hover:text-teal-700 underline"
        >
          Si tarda mucho, haz click aqui para recargar
        </button>
      </div>
    );
  }

  const initials = `${(user.nombre || '')[0] || ''}${(user.apellido || '')[0] || ''}`.toUpperCase();
  const userModules = user?.modules || [];

  const canAccess = (mod) => {
    if (mod.isProfile || mod.isApp) return true;
    if (mod.id === 13) return true;  // Reporte de Incidente: disponible para todos los trabajadores
    return userModules.includes(mod.id);
  };

  const handleSelectModule = (mod) => {
    if (!canAccess(mod)) return;
    if (mod.isApp) { setShowAppModal(true); return; }
    if (mod.externalUrl) { window.open(mod.externalUrl, '_blank'); return; }
    if (mod.route) navigate(mod.route);
  };

  const openSelModal = () => {
    if (!selData) return;
    setDraftAreas(selData.selected_area_ids || []);
    setDraftActs(selData.selected_actividad_ids || []);
    setSelSearch('');
    setShowSelModal(true);
  };

  const toggleDraftArea = (id) => {
    setDraftAreas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleDraftAct = (id) => {
    setDraftActs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const saveSel = async () => {
    if (!token) return;
    setSavingSel(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/trabajador/mis-areas-actividades`,
        { area_ids: draftAreas, actividad_ids: draftActs },
        { headers }
      );
      // Refresh data
      const [sel, _r] = await Promise.all([
        axios.get(`${API}/trabajador/mis-areas-actividades`, { headers }),
        Promise.resolve(loadResumen(headers)),
      ]);
      setSelData(sel.data || null);
      setShowSelModal(false);
    } catch (e) {
      alert('No se pudo guardar tu seleccion. Intenta nuevamente.');
    } finally {
      setSavingSel(false);
    }
  };

  const openPendCapModal = async () => {
    setPendCapModal(true);
    if (pendCapList !== null) return; // cache
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/trabajador/mis-cursos`, { headers });
      const all = Array.isArray(res.data) ? res.data : (res.data?.capacitaciones || []);
      const pend = all.filter(c => !(c.registro?.aprobado) && !c.completado);
      setPendCapList(pend);
    } catch (e) {
      setPendCapList([]);
    }
  };

  const openPendLidModal = async () => {
    setPendLidModal(true);
    if (pendLidList !== null) return;
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const now = new Date();
      const res = await axios.get(`${API}/liderazgo/mis-actividades?mes=${now.getMonth() + 1}&anio=${now.getFullYear()}`, { headers });
      const all = res.data?.actividades || [];
      const pend = all.filter(a => a.estado !== 'cumplida');
      setPendLidList(pend);
    } catch (e) {
      setPendLidList([]);
    }
  };

  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-3" data-testid="portal-perfil">
      {/* Header - compacto */}
      <div className={`rounded-xl px-4 py-3 text-white ${hideInfoPanels ? 'order-1' : ''}`} style={{ backgroundColor: '#208178' }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center text-base font-bold backdrop-blur-sm shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-bold truncate">Hola, {(user.nombre || '').split(' ')[0]}!</h1>
          </div>
        </div>
      </div>

      {/* Layout: Datos Personales | Datos Laborales | Modulos Habilitados
          Si hideInfoPanels = true → solo módulos a ancho completo, al fondo */}
      <div className={hideInfoPanels ? 'order-4' : 'grid grid-cols-1 lg:grid-cols-3 gap-3'}>
        {!hideInfoPanels && (
          <>
            {/* Info Personal */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <h2 className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Datos Personales</h2>
                  <button onClick={() => { setPwdError(''); setPwdSuccess(false); setPwdForm({ actual: '', nueva: '', confirmar: '' }); setPwdModal(true); }}
                    className="inline-flex items-center gap-1 text-[10px] font-medium text-teal-700 bg-teal-50 border border-teal-100 hover:bg-teal-100 rounded-md px-1.5 py-0.5 transition-colors"
                    data-testid="btn-cambiar-password">
                    <KeyRound className="w-3 h-3" /> Cambiar clave
                  </button>
                </div>
                <InfoRow icon={CreditCard} label="RUT" value={formatRut(user.rut)} iconColor="text-teal-500" />
                <InfoRow icon={Mail} label="Email" value={user.email} iconColor="text-blue-500" />
                <InfoRow icon={Calendar} label="Fecha de Nacimiento" value={formatDate(user.fecha_nacimiento)} iconColor="text-green-500" />
                <InfoRow icon={Globe} label="Nacionalidad" value={user.nacionalidad} iconColor="text-teal-500" />
              </CardContent>
            </Card>

            {/* Info Laboral */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-3">
                <h2 className="text-[11px] font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">Datos Laborales</h2>
                <InfoRow icon={Briefcase} label="Cargo" value={user.cargo_nombre} iconColor="text-teal-500" />
                <InfoRow icon={Award} label="Nivel" value={user.cargo_nivel ? `Nivel ${user.cargo_nivel}` : null} iconColor="text-amber-500" />
                <InfoRow icon={MapPin} label="Area" value={user.area_nombre} iconColor="text-blue-500" />
                <InfoRow icon={Building2} label="Gerencia Lomas Bayas" value={user.gerencia_nombre} iconColor="text-indigo-500" />
                <InfoRow icon={Building2} label="Superintendencia Lomas Bayas" value={user.superintendencia_nombre} iconColor="text-violet-500" />
              </CardContent>
            </Card>
          </>
        )}

        {/* Modulos Habilitados */}
        <Card className="border-0 shadow-sm" style={{ backgroundColor: '#c7e3e0' }} data-testid="modulos-habilitados">
          <CardContent className="p-4">
            <h2 className="text-[11px] font-semibold mb-2 uppercase tracking-wider" style={{ color: '#208178' }}>Modulos TystaCore Habilitados</h2>
            <div className={`grid gap-2 ${hideInfoPanels ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5' : 'grid-cols-2'}`}>
              {MODULES.map(mod => {
                const Icon = mod.icon;
                const enabled = canAccess(mod);
                return (
                  <button
                    key={mod.id}
                    onClick={() => handleSelectModule(mod)}
                    disabled={!enabled || mod.disabled}
                    data-testid={`modulo-${mod.id}`}
                    className={`flex items-center gap-2 rounded-lg border px-2 py-2 text-left transition-all ${
                      enabled
                        ? 'bg-white border-slate-200 hover:shadow-sm hover:scale-[1.02] cursor-pointer'
                        : 'bg-slate-50 border-slate-100 cursor-not-allowed opacity-60'
                    }`}
                    style={enabled ? { borderLeftColor: mod.color, borderLeftWidth: '3px' } : {}}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 relative"
                      style={{
                        backgroundColor: enabled ? `${mod.color}18` : '#f1f5f9',
                        border: `2px solid ${enabled ? mod.color : '#e2e8f0'}`,
                      }}
                    >
                      <Icon className="w-4 h-4" style={{ color: enabled ? mod.color : '#94a3b8' }} />
                      {!enabled && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-slate-200 rounded-full flex items-center justify-center">
                          <Lock className="w-2 h-2 text-slate-500" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold text-slate-800 truncate leading-tight">{mod.name}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mis Areas y Actividades (auto-seleccion del trabajador) */}
      <Card className={`border-0 shadow-sm ${hideInfoPanels ? 'order-3' : ''}`} data-testid="mis-areas-actividades">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#20817820' }}>
                <Settings2 className="w-4 h-4" style={{ color: '#208178' }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Mis Areas y Capacitaciones</h3>
                <p className="text-[11px] text-slate-500">Selecciona tus areas y capacitaciones para habilitar tus cursos (Transversales + por Area + por Capacitacion).</p>
              </div>
            </div>
            <button
              onClick={openSelModal}
              disabled={!selData}
              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#208178' }}
              data-testid="editar-areas-actividades-btn"
            >
              <Settings2 className="w-3.5 h-3.5" />
              Editar seleccion
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Areas seleccionadas ({selectedAreaNames.length})</p>
              {selectedAreaNames.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Sin areas seleccionadas aun.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="chips-areas">
                  {selectedAreaNames.map((n, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ backgroundColor: '#e0f0ef', color: '#208178' }}>
                      <MapPin className="w-3 h-3" /> {n}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Capacitaciones seleccionadas ({selectedActNames.length})</p>
              {selectedActNames.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Sin capacitaciones seleccionadas aun.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="chips-actividades">
                  {selectedActNames.map((n, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-amber-100 text-amber-800">
                      <Briefcase className="w-3 h-3" /> {n}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progreso de Capacitaciones y Liderazgo */}
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-3 ${hideInfoPanels ? 'order-2' : ''}`}>
        {/* Capacitaciones */}
        <Card className="border-0 shadow-sm" data-testid="progreso-capacitaciones">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#20817820' }}>
                  <GraduationCap className="w-4 h-4" style={{ color: '#208178' }} />
                </div>
                <h3 className="text-sm font-semibold text-slate-800">Mis Capacitaciones</h3>
              </div>
              <button
                onClick={() => onSelectTab ? onSelectTab('capacitaciones') : navigate('/capacitacion-viewer')}
                className="text-[11px] font-medium hover:underline"
                style={{ color: '#208178' }}
                data-testid="ver-capacitaciones-btn"
              >
                Ver todas →
              </button>
            </div>
            {capStats ? (
              capStats.total === 0 ? (
                <p className="text-xs text-slate-500 py-2">No tienes capacitaciones asignadas.</p>
              ) : (
                <button
                  type="button"
                  onClick={openPendCapModal}
                  className="w-full flex items-center gap-4 mt-1 text-left rounded-lg hover:bg-slate-50 transition-colors px-1 py-1 group"
                  data-testid="abrir-pendientes-cap"
                  title="Click para ver pendientes"
                >
                  <Gauge value={capStats.porcentaje} color="#208178" label="completado" />
                  <div className="flex-1 space-y-1.5">
                    <Stat icon={CheckCircle2} iconColor="text-emerald-600" label="Aprobadas" value={capStats.aprobadas} total={capStats.total} />
                    <Stat icon={AlertCircle} iconColor="text-amber-600" label="Pendientes" value={capStats.total - capStats.aprobadas} total={capStats.total} />
                    {capStats.total - capStats.aprobadas > 0 && (
                      <p className="text-[10px] text-teal-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">Click para ver pendientes →</p>
                    )}
                  </div>
                </button>
              )
            ) : (
              <div className="h-24 flex items-center justify-center"><div className="animate-pulse h-3 bg-slate-100 rounded w-full"></div></div>
            )}
          </CardContent>
        </Card>

        {/* Planificador HS */}
        <Card className="border-0 shadow-sm" data-testid="progreso-liderazgo">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#a05e4920' }}>
                  <ClipboardList className="w-4 h-4" style={{ color: '#a05e49' }} />
                </div>
                <h3 className="text-sm font-semibold text-slate-800">Agenda HS <span className="font-normal text-[10px] text-slate-400">(mes actual)</span></h3>
              </div>
              {liderazgoStats && liderazgoStats.total > 0 && (
                <button
                  onClick={() => navigate('/liderazgo-viewer')}
                  className="text-[11px] font-medium text-[#a05e49] hover:underline"
                  data-testid="ver-liderazgo-btn"
                >
                  Ver actividades →
                </button>
              )}
            </div>
            {liderazgoStats === null ? (
              <div className="h-24 flex items-center justify-center"><div className="animate-pulse h-3 bg-slate-100 rounded w-full"></div></div>
            ) : liderazgoStats.total === 0 ? (
              <p className="text-xs text-slate-500 py-2">Sin actividades de liderazgo asignadas este mes.</p>
            ) : (
              <button
                type="button"
                onClick={openPendLidModal}
                className="w-full flex items-center gap-4 mt-1 text-left rounded-lg hover:bg-slate-50 transition-colors px-1 py-1 group"
                data-testid="abrir-pendientes-lid"
                title="Click para ver pendientes"
              >
                <Gauge value={liderazgoStats.porcentaje} color="#a05e49" label="cumplimiento" />
                <div className="flex-1 space-y-1.5">
                  <Stat icon={CheckCircle2} iconColor="text-emerald-600" label="Cumplidas" value={liderazgoStats.cumplidas} total={liderazgoStats.total} />
                  <Stat icon={AlertCircle} iconColor="text-amber-600" label="Pendientes" value={liderazgoStats.pendientes} total={liderazgoStats.total} />
                  {liderazgoStats.pendientes > 0 && (
                    <p className="text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#a05e49' }}>Click para ver pendientes →</p>
                  )}
                </div>
              </button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal APP Pase Digital */}
      {showAppModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowAppModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="app-modal">
            <div className="px-6 pt-6 pb-4 text-center" style={{ background: 'linear-gradient(135deg, #6C3D8E 0%, #8B5CF6 100%)' }}>
              <Fingerprint className="w-10 h-10 text-white mx-auto mb-2" />
              <h3 className="text-lg font-bold text-white">APP Pase Digital</h3>
              <p className="text-xs text-white/70 mt-1">Selecciona tu plataforma para descargar</p>
            </div>
            <div className="p-6 space-y-3">
              <a href="https://apps.apple.com/cl/app/credencial-digital-tysta/id6756027006" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-[#6C3D8E] hover:shadow-md transition-all group"
                data-testid="download-apple">
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Descargar en</p>
                  <p className="text-base font-bold text-slate-800 group-hover:text-[#6C3D8E] transition-colors">App Store</p>
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=cl.legav.pase_digital.tysta" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-[#6C3D8E] hover:shadow-md transition-all group"
                data-testid="download-android">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current"><path d="M17.523 15.341a.667.667 0 0 0 0-1.152l-1.665-.962 1.665-.962a.667.667 0 0 0 0-1.152L6.477 4.59a.667.667 0 0 0-1 .577v13.667a.667.667 0 0 0 1 .577l11.046-4.07zM3.5 2h1a.5.5 0 0 1 .5.5v19a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-19a.5.5 0 0 1 .5-.5z"/></svg>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Disponible en</p>
                  <p className="text-base font-bold text-slate-800 group-hover:text-[#6C3D8E] transition-colors">Google Play</p>
                </div>
              </a>
            </div>
            <div className="px-6 pb-5">
              <button onClick={() => setShowAppModal(false)} className="w-full py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors" data-testid="close-app-modal">Cerrar</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal seleccion Areas y Actividades */}
      {showSelModal && selData && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => !savingSel && setShowSelModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-testid="sel-modal">
            <div className="px-5 py-4 text-white" style={{ backgroundColor: '#208178' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold">Selecciona tus Areas y Actividades</h3>
                  <p className="text-xs text-white/80 mt-0.5">Marca todas las que aplican. Esto habilitara tus cursos automaticamente.</p>
                </div>
                <button onClick={() => !savingSel && setShowSelModal(false)} className="p-1 rounded-full hover:bg-white/10" data-testid="close-sel-modal">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3">
                <input
                  type="text"
                  value={selSearch}
                  onChange={e => setSelSearch(e.target.value)}
                  placeholder="Buscar por nombre..."
                  className="w-full rounded-md px-3 py-2 text-xs text-slate-800 outline-none"
                  data-testid="sel-search"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 overflow-y-auto flex-1">
              {/* Areas */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Areas ({draftAreas.length}/{filteredAreas.length})</h4>
                  <div className="flex gap-2">
                    <button onClick={() => setDraftAreas(filteredAreas.map(a => a.id))} className="text-[10px] text-teal-600 hover:underline" data-testid="sel-areas-all">Todas</button>
                    <button onClick={() => setDraftAreas([])} className="text-[10px] text-slate-500 hover:underline" data-testid="sel-areas-none">Ninguna</button>
                  </div>
                </div>
                <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
                  {filteredAreas.map(a => {
                    const checked = draftAreas.includes(a.id);
                    const isPrincipal = selData.area_principal_id === a.id;
                    return (
                      <label key={a.id} className={`flex items-center gap-2 rounded-md border px-2 py-1.5 cursor-pointer transition-colors ${checked ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`area-opt-${a.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleDraftArea(a.id)} className="w-3.5 h-3.5 accent-teal-600" />
                        <span className="text-xs text-slate-800 flex-1 truncate">{a.nombre}</span>
                        {isPrincipal && <span className="text-[9px] uppercase font-bold text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded">Principal</span>}
                      </label>
                    );
                  })}
                  {filteredAreas.length === 0 && <p className="text-xs text-slate-400 italic py-3 text-center">Sin resultados.</p>}
                </div>
              </div>

              {/* Actividades */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Actividades ({draftActs.length}/{filteredActs.length})</h4>
                  <div className="flex gap-2">
                    <button onClick={() => setDraftActs(filteredActs.map(a => a.id))} className="text-[10px] text-amber-600 hover:underline" data-testid="sel-acts-all">Todas</button>
                    <button onClick={() => setDraftActs([])} className="text-[10px] text-slate-500 hover:underline" data-testid="sel-acts-none">Ninguna</button>
                  </div>
                </div>
                <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
                  {filteredActs.map(a => {
                    const checked = draftActs.includes(a.id);
                    return (
                      <label key={a.id} className={`flex items-center gap-2 rounded-md border px-2 py-1.5 cursor-pointer transition-colors ${checked ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`act-opt-${a.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleDraftAct(a.id)} className="w-3.5 h-3.5 accent-amber-600" />
                        <span className="text-xs text-slate-800 flex-1 truncate">{a.nombre}</span>
                      </label>
                    );
                  })}
                  {filteredActs.length === 0 && <p className="text-xs text-slate-400 italic py-3 text-center">Sin resultados.</p>}
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between gap-2 bg-slate-50">
              <p className="text-[11px] text-slate-500">Al guardar se habilitaran los cursos: Transversales + Por Area + Por Actividad.</p>
              <div className="flex items-center gap-2">
                <button onClick={() => !savingSel && setShowSelModal(false)} disabled={savingSel} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-md transition-colors" data-testid="cancel-sel-btn">
                  Cancelar
                </button>
                <button onClick={saveSel} disabled={savingSel} className="inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-60" style={{ backgroundColor: '#208178' }} data-testid="save-sel-btn">
                  {savingSel ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  {savingSel ? 'Guardando...' : 'Guardar seleccion'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Pendientes Capacitaciones */}
      {pendCapModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setPendCapModal(false)} data-testid="pend-cap-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 text-white flex items-center justify-between shrink-0" style={{ backgroundColor: '#208178' }}>
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <GraduationCap className="w-4 h-4" />
                  Capacitaciones pendientes
                </h3>
                <p className="text-[11px] text-white/80 mt-0.5">
                  Te faltan {pendCapList?.length ?? '...'} capacitaciones para llegar al 100%
                </p>
              </div>
              <button onClick={() => setPendCapModal(false)} className="p-1 rounded-full hover:bg-white/10">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {pendCapList === null ? (
                <div className="py-8 text-center text-xs text-slate-400">Cargando...</div>
              ) : pendCapList.length === 0 ? (
                <div className="py-10 text-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-700">¡Estas al dia!</p>
                  <p className="text-xs text-slate-500 mt-1">No tienes capacitaciones pendientes.</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {pendCapList.map((c, i) => (
                    <li key={c.id || i} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50 transition-colors">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-xs" style={{ backgroundColor: '#20817820', color: '#208178' }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{c.nombre}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {c.es_transversal && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">Transversal</span>}
                          {c.obligatoria && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">Obligatoria</span>}
                          {c.categoria && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{c.categoria}</span>}
                          {c.horas && <span className="text-[10px] text-slate-500">· {c.horas} hrs</span>}
                          {c.modalidad && <span className="text-[10px] text-slate-500">· {c.modalidad}</span>}
                        </div>
                      </div>
                      <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-1" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-t px-5 py-3 flex justify-between items-center bg-slate-50 shrink-0">
              <p className="text-[11px] text-slate-500">
                {pendCapList && pendCapList.length > 0 && `${pendCapList.length} curso${pendCapList.length > 1 ? 's' : ''} por completar`}
              </p>
              <div className="flex gap-2">
                <button onClick={() => setPendCapModal(false)} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-md">Cerrar</button>
                {pendCapList && pendCapList.length > 0 && (
                  <button
                    onClick={() => { setPendCapModal(false); navigate('/capacitaciones'); }}
                    className="inline-flex items-center gap-1 rounded-md px-4 py-1.5 text-xs font-semibold text-white"
                    style={{ backgroundColor: '#208178' }}
                    data-testid="ir-capacitaciones"
                  >
                    Ir a Capacitaciones →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Pendientes Planificador HS */}
      {pendLidModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setPendLidModal(false)} data-testid="pend-lid-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 text-white flex items-center justify-between shrink-0" style={{ backgroundColor: '#a05e49' }}>
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <ClipboardList className="w-4 h-4" />
                  Actividades de Liderazgo pendientes
                </h3>
                <p className="text-[11px] text-white/80 mt-0.5">
                  Te faltan {pendLidList?.length ?? '...'} actividades para llegar al 100% este mes
                </p>
              </div>
              <button onClick={() => setPendLidModal(false)} className="p-1 rounded-full hover:bg-white/10">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {pendLidList === null ? (
                <div className="py-8 text-center text-xs text-slate-400">Cargando...</div>
              ) : pendLidList.length === 0 ? (
                <div className="py-10 text-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-700">¡Todas cumplidas!</p>
                  <p className="text-xs text-slate-500 mt-1">No tienes actividades de liderazgo pendientes este mes.</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {pendLidList.map((a, i) => (
                    <li key={a.id || i} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50 transition-colors">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-xs" style={{ backgroundColor: '#a05e4920', color: '#a05e49' }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800">{a.tipo_actividad} {a.numero && `#${a.numero}`}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${a.estado === 'atrasada' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {(a.estado || 'pendiente').toUpperCase()}
                          </span>
                          {a.fecha_programada && <span className="text-[10px] text-slate-500">Programada: {a.fecha_programada.split('-').reverse().join('-')}</span>}
                          {a.observaciones && <span className="text-[10px] text-slate-500 truncate max-w-[200px]">· {a.observaciones}</span>}
                        </div>
                      </div>
                      <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-1" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border-t px-5 py-3 flex justify-between items-center bg-slate-50 shrink-0">
              <p className="text-[11px] text-slate-500">
                {pendLidList && pendLidList.length > 0 && `${pendLidList.length} actividad${pendLidList.length > 1 ? 'es' : ''} por completar`}
              </p>
              <div className="flex gap-2">
                <button onClick={() => setPendLidModal(false)} className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 rounded-md">Cerrar</button>
                {pendLidList && pendLidList.length > 0 && (
                  <button
                    onClick={() => { setPendLidModal(false); navigate('/liderazgo-viewer'); }}
                    className="inline-flex items-center gap-1 rounded-md px-4 py-1.5 text-xs font-semibold text-white"
                    style={{ backgroundColor: '#a05e49' }}
                    data-testid="ir-liderazgo"
                  >
                    Ir a Agenda HS →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Cambiar Contraseña */}
      {pwdModal && createPortal(
        <div style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          onClick={() => !pwdBusy && setPwdModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()} data-testid="modal-cambiar-password">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
                <KeyRound className="w-4 h-4 text-teal-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-slate-900">Cambiar contraseña</h3>
                <p className="text-xs text-slate-500 mt-0.5">Ingrese su clave actual y elija una nueva.</p>
              </div>
              <button onClick={() => !pwdBusy && setPwdModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
            {pwdSuccess ? (
              <div className="py-6 flex flex-col items-center justify-center text-emerald-600">
                <CheckCircle2 className="w-10 h-10 mb-1" />
                <p className="text-sm font-semibold">Contraseña actualizada</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Contraseña actual</label>
                  <input type="password" autoComplete="current-password"
                    value={pwdForm.actual} onChange={e => setPwdForm({ ...pwdForm, actual: e.target.value })}
                    className="w-full mt-0.5 px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    data-testid="pwd-actual-input" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Nueva contraseña</label>
                  <input type="password" autoComplete="new-password"
                    value={pwdForm.nueva} onChange={e => setPwdForm({ ...pwdForm, nueva: e.target.value })}
                    className="w-full mt-0.5 px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    data-testid="pwd-nueva-input" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Confirmar nueva contraseña</label>
                  <input type="password" autoComplete="new-password"
                    value={pwdForm.confirmar} onChange={e => setPwdForm({ ...pwdForm, confirmar: e.target.value })}
                    className="w-full mt-0.5 px-2.5 py-1.5 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    data-testid="pwd-confirmar-input" />
                </div>
                {pwdError && (
                  <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1" data-testid="pwd-error">{pwdError}</p>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setPwdModal(false)} disabled={pwdBusy}
                    className="px-3 py-1.5 text-xs border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50">
                    Cancelar
                  </button>
                  <button type="button" onClick={submitPwd} disabled={pwdBusy}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-teal-600 hover:bg-teal-700 text-white rounded-md font-semibold disabled:opacity-50"
                    data-testid="pwd-submit-btn">
                    {pwdBusy ? <><Loader2 className="w-3 h-3 animate-spin" /> Guardando...</> : <><Check className="w-3 h-3" /> Guardar</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};


// ===== Gauge tipo velocimetro 0-100 =====
function Gauge({ value, color, label, size = 120 }) {
  const clamped = Math.max(0, Math.min(100, Number(value) || 0));
  const radius = 46;
  const cx = 60;
  const cy = 60;
  // Arco de 180deg desde -180 (izq) a 0 (der)
  const startAngle = -180;
  const endAngle = 0;
  const sweep = endAngle - startAngle; // 180
  const valueAngle = startAngle + (sweep * clamped) / 100;

  const polar = (ang) => {
    const rad = (Math.PI * ang) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  };

  // Track background
  const trackStart = polar(startAngle);
  const trackEnd = polar(endAngle);
  const trackPath = `M ${trackStart.x} ${trackStart.y} A ${radius} ${radius} 0 0 1 ${trackEnd.x} ${trackEnd.y}`;

  // Value arc
  const valueEnd = polar(valueAngle);
  const largeArc = clamped > 50 ? 1 : 0;
  const valuePath = `M ${trackStart.x} ${trackStart.y} A ${radius} ${radius} 0 ${largeArc} 1 ${valueEnd.x} ${valueEnd.y}`;

  // Needle (desde centro hasta borde)
  const needleEnd = polar(valueAngle);
  // Tick marks cada 25%
  const ticks = [0, 25, 50, 75, 100].map(p => {
    const a = startAngle + (sweep * p) / 100;
    const p1 = polar(a);
    const rad = (Math.PI * a) / 180;
    const p2 = {
      x: cx + (radius - 7) * Math.cos(rad),
      y: cy + (radius - 7) * Math.sin(rad),
    };
    return { p, p1, p2 };
  });

  return (
    <div className="flex flex-col items-center shrink-0" style={{ width: size }}>
      <svg width={size} height={size * 0.72} viewBox="0 0 120 72">
        {/* Track */}
        <path d={trackPath} fill="none" stroke="#e2e8f0" strokeWidth="10" strokeLinecap="round" />
        {/* Value arc */}
        <path d={valuePath} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" />
        {/* Ticks */}
        {ticks.map((t, i) => (
          <line key={i} x1={t.p1.x} y1={t.p1.y} x2={t.p2.x} y2={t.p2.y} stroke="#cbd5e1" strokeWidth="1.2" />
        ))}
        {/* Needle */}
        <line x1={cx} y1={cy} x2={needleEnd.x} y2={needleEnd.y} stroke="#1e293b" strokeWidth="2" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="3.5" fill="#1e293b" />
        {/* Labels 0 y 100 */}
        <text x="10" y="68" fontSize="8" fill="#94a3b8">0</text>
        <text x="105" y="68" fontSize="8" fill="#94a3b8">100</text>
      </svg>
      <div className="text-center -mt-2">
        <p className="text-2xl font-bold leading-none" style={{ color }}>{clamped}%</p>
        <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wide">{label}</p>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, iconColor, label, value, total }) {
  return (
    <div className="flex items-center justify-between px-2 py-1.5 rounded-md bg-slate-50/80">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
        <Icon className={`w-3 h-3 ${iconColor}`} />
        <span>{label}</span>
      </div>
      <div className="text-right">
        <span className="text-sm font-bold text-slate-800">{value}</span>
        {typeof total === 'number' && <span className="text-[10px] text-slate-400"> / {total}</span>}
      </div>
    </div>
  );
}
